import React, {
   useState,
   useMemo,
   memo,
   useCallback,
   ChangeEvent,
   useEffect,
} from 'react'
import { Col, Row } from 'react-grid-system'

import * as styles from './addToKitModal.module.scss'
import { Input, Dropdown } from '../../form/fields'
import { Button, Modal } from '../..'
import { AddToKitModalProps } from './types'
import { Option } from '../../form/fields/dropdown/types'
import { ToolTypes } from '../../../../types'

const AddToKitModal = ({
   isModalOpen,
   handleClose,
   handleAddToKit,
   setAddToKitQty,
   cancel,
   kitOptions,
   addToKitQty,
   itemForm,
   warehouseOptions,
   selectedWarehouse,
   handleWarehouseChange,
   disabledWarehouseSelection,
   selectedItemNotificationId,
}: AddToKitModalProps) => {
   const [query, setQuery] = useState<object>({})
   const [selectedKit, setSelectedKit] = useState<Option | null>(null)

   const isQuantityOrConsumable = useMemo(
      () =>
         itemForm?.ToolType === ToolTypes['Consumable'] ||
         itemForm?.ToolType === ToolTypes['Quantity Item'],
      [itemForm]
   )

   useEffect(() => {
      if (!isQuantityOrConsumable) {
         setAddToKitQty(1)
      }
   }, [setAddToKitQty, isQuantityOrConsumable])

   useEffect(() => {
      if (isQuantityOrConsumable && selectedWarehouse) {
         setAddToKitQty(selectedWarehouse.quantity)
         setSelectedKit(null)
      }
   }, [selectedWarehouse, isQuantityOrConsumable])

   const handleWarehouseDropdownChange = useCallback(
      (option: Option) => {
         setSelectedKit(null)
         handleWarehouseChange(option)
      },
      [handleWarehouseChange]
   )

   const handleKitChange = useCallback(
      (kitSelected: Option) => {
         setSelectedKit(kitSelected)
         setQuery({
            KitID: Number(kitSelected.value),
            KitItems: [
               {
                  KitItemID: itemForm.Id,
                  Quantity: addToKitQty,
                  ToolLoanId: itemForm.ToolLoanId,
                  ToolWarehouseID:
                     selectedWarehouse?.toolWarehouseId ??
                     itemForm?.Warehouse?.ToolWarehouseID,
                  NotificationID: selectedItemNotificationId,
               },
            ],
         })
      },
      [
         setSelectedKit,
         setQuery,
         itemForm,
         addToKitQty,
         selectedWarehouse,
         selectedItemNotificationId,
      ]
   )

   const handleQuantityChange = useCallback(
      (e: ChangeEvent<HTMLFormElement>) => {
         const quantity = Number(e.target.value)
         setAddToKitQty(quantity)

         setQuery({
            KitID: Number(selectedKit?.value),
            KitItems: [
               {
                  KitItemID: itemForm.Id,
                  Quantity: quantity,
                  ToolLoanId: itemForm.ToolLoanId,
                  ToolWarehouseID:
                     selectedWarehouse?.toolWarehouseId ??
                     itemForm?.Warehouse?.ToolWarehouseID ??
                     itemForm?.ToolWarehouseID,
                  NotificationID: selectedItemNotificationId,
               },
            ],
         })
      },
      [
         setAddToKitQty,
         setQuery,
         selectedKit,
         itemForm,
         selectedWarehouse,
         selectedItemNotificationId,
      ]
   )

   const defaultWarehouse = useMemo(
      () =>
         selectedWarehouse
            ? warehouseOptions.find(
                 (wh) => Number(wh.value) === selectedWarehouse?.id
              )
            : null,
      [selectedWarehouse, warehouseOptions]
   )

   const maximumQuantity: number = useMemo(
      () =>
         isQuantityOrConsumable && selectedWarehouse
            ? selectedWarehouse?.quantity
            : itemForm?.StatusQuantity,
      [itemForm, isQuantityOrConsumable, selectedWarehouse]
   )

   const isQuantityValid = useMemo(
      () => addToKitQty && addToKitQty <= maximumQuantity,
      [addToKitQty, maximumQuantity]
   )

   const quantityFieldError = useMemo(
      () =>
         !isQuantityValid
            ? `There is a maximum quantity of ${maximumQuantity} available for this item.`
            : null,
      [isQuantityValid, maximumQuantity]
   )

   const kitFieldError = useMemo(() => {
      if (kitOptions?.length > 0) return null

      if (!isQuantityOrConsumable) {
         return 'There are no kits available for this item.'
      }

      if (selectedWarehouse) {
         return 'There are no kits available for this warehouse. Please select another warehouse.'
      }

      return null
   }, [selectedWarehouse, kitOptions, isQuantityOrConsumable])

   const isButtonDisabled = useMemo(
      () =>
         isQuantityOrConsumable
            ? !isQuantityValid || !selectedKit || !selectedWarehouse
            : !selectedKit,
      [isQuantityOrConsumable, isQuantityValid, selectedKit, selectedWarehouse]
   )

   return (
      <Modal
         isModalVisible={isModalOpen}
         closeModal={handleClose}
         title="Add to Kit"
         zIndex="999"
         stickyHeader
      >
         <div className={styles.content}>
            <Row>
               <Col xs={12}>
                  {isQuantityOrConsumable && (
                     <>
                        <Dropdown
                           defaultValue={defaultWarehouse}
                           name="Location"
                           options={warehouseOptions}
                           id="Location"
                           label="Warehouse"
                           placeholder="Warehouse"
                           onChange={handleWarehouseDropdownChange}
                           isClearable={false}
                           isMulti={false}
                           className={styles.warehouseDropdown}
                           menuPosition="fixed"
                           required
                           disabled={disabledWarehouseSelection}
                        />
                        <Input
                           id="KitQuantity"
                           onChange={handleQuantityChange}
                           label="Kit Quantity"
                           value={addToKitQty}
                           required
                           inputProps={{
                              type: 'number',
                              max: maximumQuantity,
                              value: addToKitQty,
                           }}
                           error={quantityFieldError}
                        />
                     </>
                  )}
                  <Dropdown
                     isMulti={false}
                     defaultValue={selectedKit}
                     id="SelectKit"
                     label="Select Kit"
                     placeholder="Select Kit"
                     name="SelectKit"
                     onChange={handleKitChange}
                     options={kitOptions}
                     isClearable={false}
                     disabled={isQuantityOrConsumable && !selectedWarehouse}
                     className={styles.kitDropdown}
                     menuPosition="fixed"
                     required
                     error={kitFieldError}
                  />
               </Col>
            </Row>
            <Row>
               <Col xs={12} sm={6}>
                  <Button
                     minWidth="100%"
                     variant="primary"
                     disabled={isButtonDisabled}
                     onClick={() => handleAddToKit(query)}
                  >
                     Add to Kit
                  </Button>
               </Col>
               <Col xs={12} sm={6}>
                  <Button minWidth="100%" variant="tertiary" onClick={cancel}>
                     Cancel
                  </Button>
               </Col>
            </Row>
         </div>
      </Modal>
   )
}

export default memo(AddToKitModal)
